import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Ban, RefreshCw, Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Orders() {
  const { company, user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const currency = company?.currency || "FCFA";

  const load = async () => {
    try {
      const { data } = await api.get("/tickets");
      setTickets(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = tickets.filter((t) => {
    if (filter === "reservation" && t.kind !== "reservation") return false;
    if (filter === "realtime" && t.kind !== "realtime") return false;
    if (filter === "pending" && t.status !== "confirmed") return false;
    if (q) {
      const s = q.toLowerCase();
      if (![t.ticket_number, t.customer_firstname, t.customer_name, t.customer_phone, t.from_city, t.to_city]
        .filter(Boolean).some((v) => v.toLowerCase().includes(s))) return false;
    }
    return true;
  });

  const cancel = async (id) => {
    if (!confirm("Annuler ce billet ?")) return;
    try { await api.post(`/tickets/${id}/cancel`); toast.success("Billet annulé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const refund = async (id) => {
    if (!confirm("Rembourser ce billet ?")) return;
    try { await api.post(`/tickets/${id}/refund`); toast.success("Remboursé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Commandes</h1>
        <p className="text-sm text-slate-500">Réservations, billets temps réel et en cours</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all" data-testid="filter-all">Tous ({tickets.length})</TabsTrigger>
            <TabsTrigger value="reservation" data-testid="filter-reservation">Réservations</TabsTrigger>
            <TabsTrigger value="realtime" data-testid="filter-realtime">Temps réel</TabsTrigger>
            <TabsTrigger value="pending" data-testid="filter-pending">En cours</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 min-w-[220px]" data-testid="orders-search-input" />
        </div>
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="text-left p-3">N°</th>
                <th className="text-left">Type</th>
                <th className="text-left">Trajet</th>
                <th className="text-left">Client</th>
                <th className="text-left">Date</th>
                <th className="text-left">Statut</th>
                <th className="text-right">Total</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {filtered.map((t) => (
                <tr key={t.id} data-testid={`ticket-row-${t.ticket_number}`} className="hover:bg-slate-50 dark:hover:bg-slate-900/50">
                  <td className="p-3 font-mono text-xs">{t.ticket_number}</td>
                  <td><Badge variant="outline">{t.kind === "reservation" ? "Réservation" : "Temps réel"}</Badge></td>
                  <td>{t.from_city} → {t.to_city}</td>
                  <td>
                    <div>{t.customer_firstname} {t.customer_name}</div>
                    <div className="text-xs text-slate-500">{t.customer_phone}</div>
                  </td>
                  <td className="text-xs">{t.date} {t.schedule}</td>
                  <td><StatusBadge status={t.status} /></td>
                  <td className="text-right font-semibold">{formatCurrency(t.total, currency)}</td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <a href={`${API_BASE}/tickets/${t.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`pdf-${t.ticket_number}`}>
                        <Button variant="ghost" size="icon" title="PDF"><Download className="w-4 h-4" /></Button>
                      </a>
                      {t.status === "confirmed" && (
                        <Button variant="ghost" size="icon" onClick={() => cancel(t.id)} title="Annuler" data-testid={`cancel-${t.ticket_number}`}><Ban className="w-4 h-4" /></Button>
                      )}
                      {t.status === "confirmed" && user?.role !== "staff" && (
                        <Button variant="ghost" size="icon" onClick={() => refund(t.id)} title="Rembourser" data-testid={`refund-${t.ticket_number}`}><RefreshCw className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-slate-500">Aucune commande</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    confirmed: { label: "Confirmé", cls: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300" },
    used: { label: "Utilisé", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
    cancelled: { label: "Annulé", cls: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300" },
    refunded: { label: "Remboursé", cls: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
  };
  const s = map[status] || map.confirmed;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.cls}`}>{s.label}</span>;
}
